import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "../swof/api";
import { AdminShell } from "./ProtectedRoute";

const knopStijl = {
  fontFamily: "var(--mono)", fontSize: 10, letterSpacing: "0.2em", textTransform: "uppercase",
  color: "var(--gold)", border: "1px solid rgba(201,169,97,0.4)", padding: "10px 16px",
  background: "transparent",
};

const TYPE_LABELS = {
  logo_splash: "Logo-openingsscherm",
  hero: "Hero",
  waarom: "Waarom nu",
  video: "Video",
  onderscheiding: "De onderscheiding",
  winnaars: "Winnaars",
  galerij: "De avond in beeld",
  verslag: "Verslag",
  aftermovie: "Aftermovie",
  bestuur: "Bestuur",
  gala: "Het gala",
  plek: "De plek",
  signup: "Blijf op de hoogte",
  partners: "Partners",
  fotogalerie: "Fotogalerie (overlay)",
  tekst: "Tekstblok",
  fotoblok: "Fotoblok",
};

function stripHtml(s = "") {
  const el = document.createElement("div");
  el.innerHTML = s.replace(/<br\s*\/?>/gi, " ");
  return (el.textContent || "").trim();
}

function firstLine(block) {
  const i = block.inhoud || {};
  return stripHtml(i.kop || i.tagline || i.datum_regel || i.kleine_kop || "") || "—";
}

const STAAT_LABEL = {
  gelijk: "Live",
  gewijzigd: "In de wachtrij",
  nieuw: "Nieuw · wachtrij",
  niet_gepubliceerd: "Nog niet live",
};

function PublicatieCel({ staat, bezig, onPubliceer, type }) {
  if (!staat) return <span />;
  const live = staat === "gelijk";
  return (
    <span style={{ display: "flex", flexDirection: "column", gap: 6, alignItems: "flex-start" }}>
      <span data-testid={`admin-block-staat-${type}`} style={{
        fontFamily: "var(--mono)", fontSize: 9, letterSpacing: "0.14em", textTransform: "uppercase",
        color: live ? "rgba(243,234,215,0.45)" : "var(--gold)",
      }}>{STAAT_LABEL[staat]}</span>
      {!live && (
        <button onClick={onPubliceer} disabled={bezig} data-testid={`admin-block-publiceer-${type}`}
          style={{
            fontFamily: "var(--mono)", fontSize: 9, letterSpacing: "0.16em",
            textTransform: "uppercase", color: "var(--ink)", background: "var(--gold)",
            border: "none", padding: "7px 10px", cursor: bezig ? "wait" : "pointer",
          }}>Publiceren</button>
      )}
    </span>
  );
}

export default function AdminPage() {
  const [content, setContent] = useState(null);
  const [blocks, setBlocks] = useState([]);
  const [sleep, setSleep] = useState(null);
  const [standen, setStanden] = useState([]);
  const [publicatie, setPublicatie] = useState(null);
  const [bezig, setBezig] = useState(false);
  const [fout, setFout] = useState("");

  const laad = () => Promise.all([
    api.get("/admin/site"),
    api.get("/admin/publicatie"),
  ]).then(([site, pub]) => {
    setContent(site.data);
    setBlocks(site.data.blocks);
    setPublicatie(pub.data);
  });

  useEffect(() => {
    document.body.classList.add("swof");
    laad();
    api.get("/admin/standen").then(({ data }) => setStanden(data));
    return () => document.body.classList.remove("swof");
  }, []);

  const zetStand = async (naam) => {
    const stand = standen.find((s) => s.naam === naam);
    if (!stand) return;
    if (!window.confirm(`Stand “${stand.label}” toepassen?\n\n${stand.uitleg}`)) return;
    try {
      await api.post(`/admin/standen/${naam}`);
      await laad();
    } catch (e) {
      meldFout(e);
    }
  };

  const meldFout = (e) => setFout(formatApiErrorDetail(e.response?.data?.detail) || e.message);

  const publiceerAlles = async () => {
    const n = publicatie?.wachtend ?? 0;
    if (!window.confirm(`Alles publiceren?\n\n${n} blok(ken) wacht(en) op publicatie. Vanaf dat moment ziet de bezoeker precies wat er nu in het concept staat.`)) return;
    setBezig(true);
    try {
      await api.post("/admin/publiceer");
      await laad();
    } catch (e) { meldFout(e); } finally { setBezig(false); }
  };

  const publiceerBlok = async (b) => {
    if (!window.confirm(`Alleen dit blok publiceren?\n\nDe rest van de site blijft staan zoals hij nu live is.`)) return;
    setBezig(true);
    try {
      await api.post(`/admin/publiceer/${b.id}`);
      await laad();
    } catch (e) { meldFout(e); } finally { setBezig(false); }
  };

  const wissel = async (b) => {
    setBlocks((lijst) => lijst.map((x) => (x.id === b.id ? { ...x, zichtbaar: !x.zichtbaar } : x)));
    try {
      await api.patch(`/admin/blocks/${b.id}/zichtbaar`, { zichtbaar: !b.zichtbaar });
    } catch (e) {
      meldFout(e);
      laad();
    }
  };

  const laatVallen = async (naarIndex) => {
    if (sleep === null || sleep === naarIndex) return setSleep(null);
    const nieuw = [...blocks];
    const [verplaatst] = nieuw.splice(sleep, 1);
    nieuw.splice(naarIndex, 0, verplaatst);
    const genummerd = nieuw.map((b, i) => ({ ...b, positie: i + 1 }));
    setBlocks(genummerd);
    setSleep(null);
    try {
      await api.post("/admin/blocks/reorder", { order: genummerd.map((b) => b.id) });
    } catch (e) {
      meldFout(e);
      laad();
    }
  };

  const voegToe = async (type) => {
    setBezig(true);
    try {
      await api.post("/admin/blocks", { type });
      await laad();
    } catch (e) {
      meldFout(e);
    } finally {
      setBezig(false);
    }
  };

  const verwijder = async (b) => {
    if (!window.confirm(`Blok ${String(b.positie).padStart(2, "0")} verwijderen?`)) return;
    try {
      await api.delete(`/admin/blocks/${b.id}`);
      await laad();
    } catch (e) {
      meldFout(e);
    }
  };

  const wachtend = publicatie?.wachtend ?? 0;
  const staten = (publicatie?.blokken || []).reduce((acc, b) => { acc[b.id] = b; return acc; }, {});

  const actions = (
    <>
      <button onClick={publiceerAlles} disabled={bezig || wachtend === 0}
        data-testid="admin-publiceer-alles" style={{
          fontFamily: "var(--mono)", fontSize: 10, letterSpacing: "0.2em", textTransform: "uppercase",
          padding: "11px 18px", border: "none", cursor: wachtend === 0 ? "default" : "pointer",
          background: wachtend === 0 ? "rgba(201,169,97,0.25)" : "var(--gold)",
          color: wachtend === 0 ? "rgba(243,234,215,0.6)" : "var(--ink)",
        }}>
        {wachtend === 0 ? "Alles gepubliceerd" : `Alles publiceren (${wachtend})`}
      </button>
      <select data-testid="admin-stand" value="" onChange={(e) => zetStand(e.target.value)}
        style={{ ...knopStijl, padding: "10px 12px" }}>
        <option value="" style={{ color: "#111" }}>Stand kiezen…</option>
        {standen.map((s) => (
          <option key={s.naam} value={s.naam} style={{ color: "#111" }}>{s.label}</option>
        ))}
      </select>
      <button onClick={() => voegToe("tekst")} disabled={bezig} data-testid="admin-add-tekst" style={knopStijl}>
        + Tekstblok
      </button>
      <button onClick={() => voegToe("fotoblok")} disabled={bezig} data-testid="admin-add-fotoblok" style={knopStijl}>
        + Fotoblok
      </button>
      <Link to="/voorbeeld" data-testid="admin-preview" style={knopStijl}>Voorbeeld</Link>
    </>
  );

  return (
    <AdminShell title="Pagina · home" actions={actions}>
      <h1 style={{
        fontFamily: "var(--serif)", fontSize: "clamp(32px, 4vw, 52px)",
        fontWeight: 400, margin: "0 0 12px", letterSpacing: "-0.02em",
      }}>De blokken van de pagina</h1>
      <p style={{ fontFamily: "var(--sans)", fontSize: 14, opacity: 0.65, margin: "0 0 24px", maxWidth: "62ch" }}>
        Sleep aan het handvat om te herordenen; bij loslaten worden de posities opnieuw
        weggeschreven. Klik een kop om het blok te bewerken. Verborgen blokken blijven gedimd in
        de lijst staan.
      </p>
      <p style={{ fontFamily: "var(--sans)", fontSize: 14, opacity: 0.65, margin: "0 0 36px", maxWidth: "62ch" }}>
        Alles wat u hier doet blijft <em>concept</em>: de bezoeker ziet er niets van tot u
        publiceert. Zo zet u de blokken en teksten van 14 september rustig klaar in de wachtrij.
        Met <em>Voorbeeld</em> bekijkt u het concept, met <em>Publiceren</em> gaat het live —
        per blok of alles tegelijk.
        {publicatie && (
          <span data-testid="admin-publicatie-info" style={{ display: "block", marginTop: 10, fontFamily: "var(--mono)", fontSize: 11, opacity: 0.8 }}>
            {publicatie.laatste
              ? `Laatst gepubliceerd: ${new Date(publicatie.laatste.op).toLocaleString("nl-NL")} · ${publicatie.laatste.door_naam}`
              : "Nog nooit gepubliceerd — de site toont voorlopig het concept."}
            {" · "}
            {publicatie.wachtend === 0 ? "niets in de wachtrij" : `${publicatie.wachtend} in de wachtrij`}
          </span>
        )}
      </p>

      {fout && (
        <div data-testid="admin-fout" style={{
          marginBottom: 24, padding: "12px 14px", border: "1px solid rgba(201,80,60,0.5)",
          background: "rgba(201,80,60,0.08)", fontFamily: "var(--sans)", fontSize: 13, color: "#e8b0a4",
        }}>{fout}</div>
      )}

      {!content ? (
        <div data-testid="admin-loading" style={{ opacity: 0.5, fontFamily: "var(--mono)", fontSize: 11 }}>Laden…</div>
      ) : (
        <>
          <ul data-testid="admin-block-list" style={{ listStyle: "none", padding: 0, margin: 0, borderTop: "1px solid var(--line)" }}>
            {blocks.map((b, i) => (
              <li key={b.id} data-testid={`admin-block-${b.type}`} draggable
                onDragStart={() => setSleep(i)}
                onDragOver={(e) => e.preventDefault()}
                onDrop={() => laatVallen(i)}
                onDragEnd={() => setSleep(null)}
                style={{
                  display: "grid", gridTemplateColumns: "28px 52px 1fr 150px 150px 120px 130px 70px",
                  gap: 12, alignItems: "center", padding: "14px 8px",
                  borderBottom: "1px solid var(--line)", opacity: b.zichtbaar ? 1 : 0.45,
                  background: sleep === i ? "rgba(201,169,97,0.08)" : "transparent",
                  transition: "background .2s var(--ease)",
                }}>
                <span data-testid={`admin-block-grip-${b.type}`} title="Sleep om te herordenen" style={{
                  cursor: "grab", color: "var(--gold)", opacity: 0.6, fontSize: 15, userSelect: "none",
                }}>⠿</span>
                <span data-testid={`admin-block-positie-${b.type}`} style={{
                  fontFamily: "var(--mono)", fontSize: 12, color: "var(--gold)", letterSpacing: "0.1em",
                }}>{String(b.positie).padStart(2, "0")}</span>
                <Link to={`/admin/blok/${b.id}`} data-testid={`admin-block-edit-${b.type}`} style={{
                  fontFamily: "var(--serif)", fontSize: 19, borderBottom: "1px solid transparent",
                  transition: "color .2s var(--ease), border-color .2s var(--ease)",
                }}
                  onMouseEnter={(e) => { e.currentTarget.style.color = "var(--gold)"; e.currentTarget.style.borderBottomColor = "var(--line-strong)"; }}
                  onMouseLeave={(e) => { e.currentTarget.style.color = ""; e.currentTarget.style.borderBottomColor = "transparent"; }}
                >{firstLine(b)}</Link>
                <span style={{
                  fontFamily: "var(--mono)", fontSize: 10, letterSpacing: "0.18em",
                  textTransform: "uppercase", opacity: 0.65,
                }}>{TYPE_LABELS[b.type] || b.type}</span>
                <span data-testid={`admin-block-gewijzigd-${b.type}`} style={{
                  fontFamily: "var(--mono)", fontSize: 9, letterSpacing: "0.12em", opacity: 0.5,
                }}>
                  {b.bijgewerkt_door_naam
                    ? `${b.bijgewerkt_door_naam} · ${new Date(b.bijgewerkt_op).toLocaleDateString("nl-NL")}`
                    : "—"}
                </span>
                <button onClick={() => wissel(b)} data-testid={`admin-block-toggle-${b.type}`}
                  aria-pressed={b.zichtbaar} style={{
                    display: "flex", alignItems: "center", gap: 9, background: "transparent",
                    fontFamily: "var(--mono)", fontSize: 9, letterSpacing: "0.16em",
                    textTransform: "uppercase",
                    color: b.zichtbaar ? "var(--gold)" : "rgba(243,234,215,0.5)",
                  }}>
                  <span style={{
                    width: 30, height: 16, borderRadius: 999, position: "relative", flexShrink: 0,
                    border: `1px solid ${b.zichtbaar ? "var(--gold)" : "var(--line)"}`,
                    background: b.zichtbaar ? "rgba(201,169,97,0.25)" : "transparent",
                    transition: "background .25s var(--ease), border-color .25s var(--ease)",
                  }}>
                    <span style={{
                      position: "absolute", top: 2, left: b.zichtbaar ? 15 : 2,
                      width: 10, height: 10, borderRadius: "50%",
                      background: b.zichtbaar ? "var(--gold)" : "rgba(243,234,215,0.45)",
                      transition: "left .25s var(--ease), background .25s var(--ease)",
                    }} />
                  </span>
                  {b.zichtbaar ? "Zichtbaar" : "Verborgen"}
                </button>
                <PublicatieCel staat={staten[b.id]?.staat} bezig={bezig}
                  onPubliceer={() => publiceerBlok(b)} type={b.type} />
                {["tekst", "fotoblok"].includes(b.type) ? (
                  <button onClick={() => verwijder(b)} data-testid={`admin-block-delete-${b.id}`} style={{
                    fontFamily: "var(--mono)", fontSize: 9, letterSpacing: "0.16em",
                    textTransform: "uppercase", color: "#e8b0a4",
                    border: "1px solid rgba(201,80,60,0.4)", padding: "8px 10px", background: "transparent",
                  }}>Weg</button>
                ) : <span />}
              </li>
            ))}
          </ul>

          <div style={{
            marginTop: 48, display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 16,
          }}>
            {[
              ["Inductees", content.inductees.length],
              ["Bestuursleden", content.bestuursleden.length],
              ["Partners", content.partners.length],
              ["Video's", content.videos.length],
              ["Galerijfoto's", content.galerij_fotos.length],
            ].map(([label, n]) => (
              <div key={label} style={{ border: "1px solid var(--line)", padding: "18px 20px" }}>
                <div className="eyebrow" style={{ fontSize: 10, marginBottom: 8 }}>{label}</div>
                <div style={{ fontFamily: "var(--serif)", fontSize: 30, lineHeight: 1 }}>{n}</div>
              </div>
            ))}
          </div>
        </>
      )}
    </AdminShell>
  );
}
